import type { SittingGroup } from '../lib/sitting';

/**
 * Where a matching-headings group's blanks belong.
 *
 * On the real screen the headings are dropped onto the passage itself — a slot
 * above each lettered paragraph — not onto a list beside it. The group's items
 * say which paragraph each number is ("14  Paragraph A"), so the mapping is
 * read from them rather than assumed from their order: a paper that lists the
 * paragraphs out of order, or skips one, still lands its headings correctly.
 */

const PARAGRAPH = /\b(?:paragraph|section)\s+([A-Z])\b/i;

export interface HeadingSlot {
  number: number;
  para: string;
}

/** The slots, or null when this is not a heading match onto named paragraphs. */
export function headingSlots(group: SittingGroup): HeadingSlot[] | null {
  if (group.inputMode !== 'DRAG' || group.bodyHtml) return null;

  const slots: HeadingSlot[] = [];
  for (const question of group.questions) {
    const text = (question.promptHtml ?? '').replace(/<[^>]*>/g, ' ');
    const match = PARAGRAPH.exec(text);
    // One unreadable item and the whole group falls back to answering beside
    // the questions: half a task in the passage and half beside it would be
    // worse than either.
    if (!match) return null;
    slots.push({ number: question.number, para: match[1]!.toUpperCase() });
  }
  return slots.length > 0 ? slots : null;
}

/** True when the passage really carries every paragraph the slots name. */
export const passageHasParagraphs = (html: string | null, slots: HeadingSlot[]): boolean =>
  Boolean(html) && slots.every((slot) => html!.includes(`data-para="${slot.para}"`));
